import { useTranslation } from "react-i18next";
import { CalendarDays } from "lucide-react";
import { DateInput } from "@/components/ui/DateInput";
import {
  ANALYTICS_PRESETS,
  analyticsPresetRange,
} from "@/features/reports/analyticsFilters";
import type { AnalyticsPresetKey } from "@/features/reports/analyticsFilters";
import { DEFAULT_TENANT_TZ } from "@/lib/date";
import { cn } from "@/lib/cn";

/**
 * TASK-5041: the preset keys and the range each one resolves to now live in
 * `analyticsFilters.ts` beside the page's default window, so a pill and the
 * default can no longer disagree about which day «today» is. The three local
 * helpers this replaced (`todayStr` / `daysAgoStr` / `firstOfMonthStr`) were a
 * near-copy of that module's own pair, and each one mixed browser-LOCAL day
 * arithmetic (`d.setDate(...)`) with a UTC slice (`.toISOString().slice(0,10)`)
 * inside a single function.
 *
 * Re-exported under the old name so the existing callers that type their props
 * against `PresetKey` keep compiling.
 */
export type PresetKey = AnalyticsPresetKey;

export interface AnalyticsFilterValues {
  date_from: string;
  date_to: string;
  vehicle_id: string;
  driver_id: string;
  /** Active preset pill, or "" when a custom range is selected. */
  preset: string;
}

interface VehicleOption {
  id: number;
  name: string;
}

interface DriverOption {
  id: number;
  name: string;
}

interface Props {
  values: AnalyticsFilterValues;
  vehicleOptions: VehicleOption[];
  driverOptions: DriverOption[];
  /** Merge a patch of filter values into the shared URL state. */
  onChange: (patch: Partial<AnalyticsFilterValues>) => void;
  /** Hide the driver control (e.g. Fleet Load is per-vehicle only). */
  hideDriver?: boolean;
  /** Hide the vehicle control (e.g. Reviews/Notifications have no vehicle filter). */
  hideVehicle?: boolean;
  /**
   * Optional leading controls rendered before the date inputs on the top row —
   * used by the Notifications tab for its Email/SMS channel sub-tabs so they
   * sit inside the shared bar instead of a bespoke one above it.
   */
  leadingSlot?: React.ReactNode;
  /**
   * Optional trailing controls rendered on the right of the top row — used by
   * Reviews (rating/search/"with comments") and Notifications (status/search/
   * "undelivered only") to extend the shared bar rather than replace it.
   */
  rightSlot?: React.ReactNode;
  /**
   * When true, the `rightSlot` is grouped left next to the date/driver controls
   * with the same uniform gap instead of being pushed to the far right by a
   * flex spacer. Used by Reviews so ВОДИТЕЛЬ → ОЦЕНКА are evenly spaced rather
   * than separated by a wide `justify-between`-style gap (TASK-3849).
   */
  groupRightSlot?: boolean;
  /**
   * TASK-5041: IANA zone the quick-range preset pills resolve their dates in.
   *
   * Passed IN rather than read from `useTenantTimezone()` here, following
   * `DraggableStopList.tsx:59-63`: that hook is a TanStack query, so calling it
   * would make this presentational component require a `QueryClientProvider`
   * in every tree that renders it — including the existing unit tests, which
   * render it bare. Each of the three hosts (AnalyticsPage, ReviewsTab,
   * NotificationsLogTab) already reads the zone for its own filters, so the
   * value is free there.
   *
   * Defaults to the same fallback the hook serves, so a host that has not been
   * migrated behaves exactly as before rather than crashing.
   */
  tenantTz?: string;
}

/**
 * Shared analytics toolbar (Sprint 117, TASK-3828): date-from / date-to /
 * vehicle / driver controls plus a row of quick-range preset pills. Extracted
 * from the former inline `ReportsPage` toolbar so every analytics tab that needs
 * date/vehicle/driver scoping renders the same control. State lives in the
 * Analytics shell's URL filters; this component is presentational.
 */
export function AnalyticsFilterBar({
  values,
  vehicleOptions,
  driverOptions,
  onChange,
  hideDriver = false,
  hideVehicle = false,
  leadingSlot,
  rightSlot,
  groupRightSlot = false,
  tenantTz = DEFAULT_TENANT_TZ,
}: Props) {
  const { t } = useTranslation();

  /* Labels stay spelled-out literal lookups rather than a map over
     ANALYTICS_PRESETS with an interpolated key: `npm run check:i18n` scans only
     statically-written key arguments, so a dynamic key would make these four
     invisible to the gate that catches a missing translation (the same reason
     `VehicleListPanel.tsx:59` keeps its FILTER_LABEL_KEY map explicit). The key
     ORDER is still taken from ANALYTICS_PRESETS, so the pills and
     `analyticsPresetRange` cannot drift apart. */
  const PRESET_LABELS: Record<PresetKey, string> = {
    today: t("reports.presets.today"),
    days7: t("reports.presets.days7"),
    days30: t("reports.presets.days30"),
    thisMonth: t("reports.presets.thisMonth"),
  };
  const PRESETS: { key: PresetKey; label: string }[] = ANALYTICS_PRESETS.map(
    (key) => ({ key, label: PRESET_LABELS[key] }),
  );

  /**
   * The range is computed HERE, in the handler, rather than precomputed at
   * render: it is therefore re-derived from the clock on every click, so a
   * press after the tenant's midnight emits the new day instead of a value
   * frozen at mount. `tenantTz` is read from the current render's props, so a
   * zone that resolves late (the host's hook serves `Europe/Minsk` while
   * `["settings-planning"]` loads) is picked up on the next click.
   */
  function handlePreset(preset: PresetKey) {
    const { date_from, date_to } = analyticsPresetRange(preset, tenantTz);
    onChange({ date_from, date_to, preset });
  }

  return (
    <div className="rounded-2xl border border-gray-100 bg-white shadow-sm">
      {/* Top row: optional leading slot + date range + vehicle + driver */}
      <div className="flex flex-wrap items-end gap-3 px-4 pt-4 pb-3">
        {leadingSlot}

        {/* Date from */}
        <div className="flex flex-col gap-1">
          <label className="text-[10px] font-semibold uppercase tracking-wider text-gray-400">
            {t("reports.filter.dateFrom")}
          </label>
          <div className="flex items-center gap-1.5 rounded-lg border border-gray-200 bg-gray-50 px-2.5 py-1.5">
            <CalendarDays size={13} className="shrink-0 text-gray-400" />
            <DateInput
              variant="bare"
              value={values.date_from}
              onChange={(iso) => onChange({ date_from: iso, preset: "" })}
              aria-label={t("reports.filter.dateFrom")}
              className="text-xs"
            />
          </div>
        </div>

        {/* Date to */}
        <div className="flex flex-col gap-1">
          <label className="text-[10px] font-semibold uppercase tracking-wider text-gray-400">
            {t("reports.filter.dateTo")}
          </label>
          <div className="flex items-center gap-1.5 rounded-lg border border-gray-200 bg-gray-50 px-2.5 py-1.5">
            <CalendarDays size={13} className="shrink-0 text-gray-400" />
            <DateInput
              variant="bare"
              value={values.date_to}
              min={values.date_from || undefined}
              onChange={(iso) => onChange({ date_to: iso, preset: "" })}
              aria-label={t("reports.filter.dateTo")}
              className="text-xs"
            />
          </div>
        </div>

        {/* Vehicle */}
        {!hideVehicle && (
          <div className="flex flex-col gap-1">
            <label className="text-[10px] font-semibold uppercase tracking-wider text-gray-400">
              {t("reports.filter.vehicle")}
            </label>
            <select
              value={values.vehicle_id}
              onChange={(e) => onChange({ vehicle_id: e.target.value })}
              className="h-[30px] rounded-lg border border-gray-200 bg-gray-50 px-2.5 text-xs text-gray-900 outline-none focus:border-brand-primary focus:ring-1 focus:ring-brand-primary"
            >
              <option value="">{t("reports.filter.allVehicles")}</option>
              {vehicleOptions.map((v) => (
                <option key={v.id} value={v.id}>
                  {v.name}
                </option>
              ))}
            </select>
          </div>
        )}

        {/* Driver */}
        {!hideDriver && (
          <div className="flex flex-col gap-1">
            <label className="text-[10px] font-semibold uppercase tracking-wider text-gray-400">
              {t("reports.filter.driver")}
            </label>
            <select
              value={values.driver_id}
              onChange={(e) => onChange({ driver_id: e.target.value })}
              className="h-[30px] rounded-lg border border-gray-200 bg-gray-50 px-2.5 text-xs text-gray-900 outline-none focus:border-brand-primary focus:ring-1 focus:ring-brand-primary"
            >
              <option value="">{t("reports.filter.allDrivers")}</option>
              {driverOptions.map((d) => (
                <option key={d.id} value={d.id}>
                  {d.name}
                </option>
              ))}
            </select>
          </div>
        )}

        {/* Optional trailing controls. By default a flex spacer pushes them to
            the far right; when `groupRightSlot` is set they stay grouped left
            with the same uniform gap as the other controls (even spacing). */}
        {!groupRightSlot && <div className="flex-1" />}
        {rightSlot}
      </div>

      {/* Bottom row: presets */}
      <div className="flex flex-wrap items-center gap-1.5 border-t border-gray-50 px-4 py-2.5">
        <span className="mr-1 text-[10px] font-semibold uppercase tracking-wider text-gray-400">
          {t("reports.presets.label")}
        </span>
        {PRESETS.map((preset) => (
          <button
            key={preset.key}
            type="button"
            onClick={() => handlePreset(preset.key)}
            className={cn(
              "rounded-full border px-2.5 py-0.5 text-[11px] font-semibold transition-colors",
              values.preset === preset.key
                ? "border-brand-primary bg-brand-primary text-white"
                : "border-gray-200 bg-white text-gray-500 hover:bg-gray-50",
            )}
          >
            {preset.label}
          </button>
        ))}
      </div>
    </div>
  );
}
